import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/shadcn/button"
import { RangeSlider } from "@/components/shadcn/slider/range-slider.tsx"
import { formatAgeRange, type AnimalLifeSpan } from "@/features/farm"
import {
  FILTER_AGE,
  FIVE_YEARS,
  MAX_AGE,
  MAX_FILTER_AGE,
  ONE_YEAR,
  THREE_YEARS,
} from "@/features/farm/types/animal-ages-constants.ts"
import { cn } from "@/utils/styles.ts"

interface Props {
  currentValue: AnimalLifeSpan
  onValueChange: (ageRange: AnimalLifeSpan) => void
}

const QUICK_SELECTION_AGES: AnimalLifeSpan[] = [
  { from: 0, to: ONE_YEAR },
  { from: ONE_YEAR, to: THREE_YEARS },
  { from: THREE_YEARS, to: FIVE_YEARS },
  { from: FIVE_YEARS, to: MAX_AGE },
]

const AnimalAgeDrawerFilter: React.FC<Props> = ({
  currentValue,
  onValueChange,
}) => {
  const { t } = useTranslation()
  const [ageRange, setAgeRange] = useState<AnimalLifeSpan>(
    currentValue ?? FILTER_AGE,
  )

  const handleRangeChange = (rangeArray: number[]) => {
    setAgeRange({ from: rangeArray[0], to: rangeArray[1] })
  }

  const quickSelectionButtons = [
    { age: QUICK_SELECTION_AGES[0], label: `0 ${t("years")} - 1 ${t("year")}` },
    { age: QUICK_SELECTION_AGES[1], label: `1 ${t("year")} - 3 ${t("years")}` },
    {
      age: QUICK_SELECTION_AGES[2],
      label: `3 ${t("years")} - 5 ${t("years")}`,
    },
    { age: QUICK_SELECTION_AGES[3], label: `5 ${t("years")}+` },
  ]

  return (
    <div className="flex h-full w-full flex-col justify-between gap-10 p-4 pb-8">
      <div className="flex flex-col gap-[10px]">
        <p className="text-[18px] font-semibold text-neutral-700">
          {t("filterByAge")}
        </p>

        <div>
          <p className="text-neutral-500">{t("currentInterval")}</p>
          <div className="mt-2.5 flex h-[30px] w-full items-center justify-center rounded-[8px]">
            <span className="text-[20px] font-semibold text-neutral-700">
              {formatAgeRange(ageRange)}
            </span>
          </div>
        </div>

        <div>
          <div className="mb-2 flex justify-between">
            <p>
              {0} {t("years")}
            </p>
            <p>
              {MAX_FILTER_AGE / 12} {t("years")} +
            </p>
          </div>
          <RangeSlider
            value={[ageRange.from, ageRange.to]}
            min={0}
            max={MAX_FILTER_AGE}
            defaultValue={[ageRange.from, ageRange.to]}
            onValueChange={handleRangeChange}
            step={1}
            minStepsBetweenThumbs={1}
          />
        </div>

        <div>
          <p className="pb-2 pt-5 text-neutral-500">{t("quickSelection")}</p>
          <div className="flex flex-wrap gap-4">
            {quickSelectionButtons.map((button) => {
              const isActive =
                button.age.from === ageRange.from &&
                button.age.to === ageRange.to
              return (
                <Button
                  key={button.label}
                  variant={isActive ? "default" : "secondary"}
                  size="sm"
                  className={cn(
                    "rounded-[8px]",
                    // Suppress hover/focus styles on touch devices
                    "@media (hover: none) { } pointer-events-auto hover:bg-inherit",
                    "[@media(hover:none)]:hover:bg-neutral-100",
                    isActive && "[@media(hover:none)]:hover:bg-brown-500",
                  )}
                  onClick={() => setAgeRange(button.age)}
                >
                  {button.label}
                </Button>
              )
            })}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-[10px]">
        <Button
          variant="secondary"
          size="lg"
          className="w-full rounded-[8px] [@media(hover:none)]:hover:bg-neutral-100"
          onClick={() => onValueChange(FILTER_AGE)}
        >
          {t("resetFilter")}
        </Button>
        <Button
          variant="default"
          size="lg"
          className="w-full rounded-[8px] [@media(hover:none)]:hover:bg-brown-500"
          onClick={() => onValueChange(ageRange)}
        >
          {t("showResults")}
        </Button>
      </div>
    </div>
  )
}

export default AnimalAgeDrawerFilter
